// src/pages/home/Players.js
import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import PageSectionsRenderer from "../../components/common/PageSectionsRenderer";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";
const DEFAULT_AVATAR = "/images/placeholder-user.svg";

const buildPlayerImageUrl = (rawPath, playerId) => {
  return resolveAssetUrl(rawPath, {
    folder: "players",
    entityId: playerId,
    defaultPath: DEFAULT_AVATAR,
  });
};

const normalizeApiResponse = (res) => {
  if (!res) return { players: [], sports: [] };
  if (Array.isArray(res)) return { players: res, sports: [] };

  if (res.players && Array.isArray(res.players)) {
    const sports = Array.isArray(res.sports) ? res.sports : res.sports || [];
    return { players: res.players, sports };
  }

  if (res.data && typeof res.data === "object") {
    if (Array.isArray(res.data.players) || Array.isArray(res.data)) {
      const players = Array.isArray(res.data.players)
        ? res.data.players
        : Array.isArray(res.data)
          ? res.data
          : [];
      const sports = Array.isArray(res.data.sports)
        ? res.data.sports
        : res.sports || [];
      return { players, sports };
    }
  }

  if (Array.isArray(res.data)) return { players: res.data, sports: [] };

  if (res.data && res.data.data) {
    const nested = res.data.data;
    if (Array.isArray(nested))
      return { players: nested, sports: res.data.sports || [] };
    if (nested.players && Array.isArray(nested.players)) {
      const sports = Array.isArray(nested.sports)
        ? nested.sports
        : res.data.sports || [];
      return { players: nested.players, sports };
    }
  }

  if (res.data && typeof res.data === "object") {
    const maybePlayers = res.data.players || res.data;
    const players = Array.isArray(maybePlayers) ? maybePlayers : [];
    const sports = Array.isArray(res.data.sports) ? res.data.sports : [];
    return { players, sports };
  }

  return { players: res.players || [], sports: res.sports || [] };
};

// detect if a record is a medal (1,2,3 or gold/silver/bronze)
const hasMedalInRecord = (r) => {
  const posCandidate =
    r.rank ??
    r.position ??
    r.position_secured ??
    r.result_position ??
    null;

  if (posCandidate == null) return false;

  if (typeof posCandidate === "number") {
    return [1, 2, 3].includes(Number(posCandidate));
  }

  const s = String(posCandidate).toLowerCase();
  if (s.includes("gold") || s.includes("🥇")) return true;
  if (s.includes("silver") || s.includes("🥈")) return true;
  if (s.includes("bronze") || s.includes("🥉")) return true;

  const n = parseInt(s.replace(/\D/g, ""), 10);
  return !Number.isNaN(n) && [1, 2, 3].includes(n);
};

const getInitials = (name) => {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getGradientBg = (name) => {
  const colors = [
    "from-blue-600 to-indigo-600",
    "from-emerald-500 to-teal-600",
    "from-amber-500 to-orange-600",
    "from-rose-500 to-pink-600",
    "from-purple-600 to-fuchsia-600",
    "from-cyan-500 to-blue-600"
  ];
  let hash = 0;
  const str = name || "P";
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

const Players = ({ adminView = false }) => {
  const { role } = useContext(AuthContext);
  const isAdmin = adminView && ["admin", "secretary"].includes(String(role || "").toLowerCase());
  const [saving, setSaving] = useState(false);
  const [publicEnabled, setPublicEnabled] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [visibilityMessage, setVisibilityMessage] = useState("");
  const [visibilityError, setVisibilityError] = useState("");
  const [players, setPlayers] = useState([]);
  const [sports, setSports] = useState([]);
  const [filterSport, setFilterSport] = useState("All");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchPlayers = async () => {
    setLoading(true);
    setSelectedIds([]);
    setError("");
    try {
      const params =
        filterSport && filterSport !== "All" ? { sport: filterSport } : {};
      const res = isAdmin
        ? await API.get("/admin/public-players", { params })
        : await API.fetchPlayers(params);
      const { players: fetchedPlayers, sports: fetchedSports } =
        normalizeApiResponse(res);
      setPublicEnabled(res?.public_achievements_enabled ?? true);

      const normalized = (fetchedPlayers || []).map((p) => {
        const records = Array.isArray(p.records)
          ? p.records
          : Array.isArray(p.performances)
            ? p.performances
            : Array.isArray(p.performance)
              ? p.performance
              : [];

        const candidate =
          p.photo ||
          p.photo_path ||
          p.image_path ||
          p.image ||
          p.avatar ||
          p.upload_path ||
          p.picture ||
          null;

        let hasPhoto = false;
        if (candidate) {
          const lower = candidate.toLowerCase();
          if (!lower.includes("default-user") && !lower.includes("placeholder") && !lower.includes("fallback")) {
            hasPhoto = true;
          }
        }

        const image_path = buildPlayerImageUrl(
          candidate,
          p.id || p.player_id
        );

        let age = "N/A";
        if (p.dob) {
          const yearDiff =
            new Date().getFullYear() - new Date(p.dob).getFullYear();
          age = Number.isFinite(yearDiff) ? yearDiff : "N/A";
        }

        return {
          ...p,
          records,
          image_path,
          age,
          photo: hasPhoto ? candidate : null,
        };
      });

      setPlayers(normalized);
      setSports(Array.isArray(fetchedSports) ? fetchedSports : []);
    } catch (err) {
      console.error("Error fetching players:", err);
      setError(err?.message || "Failed to load players");
      setPlayers([]);
      setSports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlayers();
    const refreshPublicView = () => {
      if (!adminView && document.visibilityState !== "hidden") fetchPlayers();
    };
    window.addEventListener("focus", refreshPublicView);
    document.addEventListener("visibilitychange", refreshPublicView);
    return () => {
      window.removeEventListener("focus", refreshPublicView);
      document.removeEventListener("visibilitychange", refreshPublicView);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSport, isAdmin, adminView]);

  const changeAchievementVisibility = async (visible, scope) => {
    setSaving(true);
    setVisibilityError("");
    setVisibilityMessage("");
    try {
      await API.put("/admin/public-players/achievements-visibility", {
        achievements_public_visible: visible,
        scope,
        ...(scope === "selected" ? { player_ids: selectedIds } : {}),
      });
      setPlayers((current) => current.map((item) =>
        scope === "all" || selectedIds.includes(item.id)
          ? { ...item, achievements_public_visible: visible } : item
      ));
      setVisibilityMessage(`Achievements ${visible ? "shown" : "hidden"} for ${scope === "all" ? "all players" : "selected players"}.`);
    } catch (err) {
      setVisibilityError(err?.response?.data?.message || err?.message || "Unable to update achievement visibility.");
    } finally {
      setSaving(false);
    }
  };

  const changePublicAchievementSetting = async (enabled) => {
    setSaving(true);
    setVisibilityError("");
    setVisibilityMessage("");
    try {
      await API.put("/admin/public-players/settings", { public_enabled: enabled });
      setPublicEnabled(enabled);
      setVisibilityMessage(enabled
        ? "Public achievements enabled. Individual player visibility settings apply."
        : "All current and future achievements are hidden from the public.");
    } catch (err) {
      setVisibilityError(err?.response?.data?.message || err?.message || "Unable to update public achievement settings.");
    } finally {
      setSaving(false);
    }
  };

  // Eligibility and sport filtering are enforced by the server.
  const filteredPlayers = isAdmin ? players : publicEnabled
    ? players.filter((player) => ![false, 0, "0"].includes(player.achievements_public_visible))
    : [];

  return (
    <>
      <Navbar />
      <main className="max-w-7xl mx-auto p-6">
        {adminView && (
          <Link to="/admin" className="inline-block text-blue-700 hover:underline mb-4">
            Back to admin dashboard
          </Link>
        )}
        <h1 className="text-4xl font-extrabold mb-4 text-center text-gray-900">
          {adminView ? "Public Achievement Visibility" : "Medal Winning Players"}
        </h1>
        {!adminView && <p className="text-center text-gray-600 mb-8">
          Celebrating the dedication and achievements of para athletes across Andhra Pradesh.
        </p>}
        {isAdmin && (
          <div className="bg-blue-50 rounded-lg p-4 mb-6 space-y-3">
            <div className="border-b border-blue-200 pb-4 space-y-2">
              <h2 className="font-semibold text-blue-900">Current and future achievements</h2>
              <p className="text-sm text-blue-800">
                {publicEnabled
                  ? "Public display is enabled. Each player's visibility setting applies to existing and newly added achievements."
                  : "Public display is disabled. All existing and newly added achievements stay hidden, including those of new players."}
                {" "}Hiding removes the player photos, names and achievement details from the public page. Admins and secretaries can still view them here.
              </p>
              <button type="button" disabled={saving || loading}
                onClick={() => changePublicAchievementSetting(!publicEnabled)}
                className="px-4 py-2 rounded-lg bg-blue-900 text-white text-sm font-semibold disabled:opacity-50">
                {publicEnabled ? "Hide current and future achievements" : "Enable public achievements"}
              </button>
              {!publicEnabled && <p role="status" className="text-sm font-semibold text-amber-800">
                The master setting overrides the player settings below. Enable public achievements to apply those settings.
              </p>}
            </div>
            <p className="text-sm text-blue-800">
              Control public player achievement cards, including photos, names and medal details.
              Select players below, or apply to all players across every sport.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-sm flex items-center gap-2">
                <input type="checkbox" disabled={loading || saving || players.length === 0}
                  checked={players.length > 0 && players.every((p) => selectedIds.includes(p.id))}
                  onChange={(e) => setSelectedIds(e.target.checked ? players.map((p) => p.id) : [])} />
                Select displayed players ({selectedIds.length} selected)
              </label>
              {[true, false].map((visible) => (
                <button key={`selected-${visible}`} type="button" disabled={saving || loading || selectedIds.length === 0}
                  onClick={() => changeAchievementVisibility(visible, "selected")}
                  className="px-3 py-2 rounded-lg bg-blue-700 text-white text-sm disabled:opacity-50">
                  {visible ? "Show" : "Hide"} selected achievements
                </button>
              ))}
              {[true, false].map((visible) => (
                <button key={`all-${visible}`} type="button" disabled={saving || loading}
                  onClick={() => changeAchievementVisibility(visible, "all")}
                  className="px-3 py-2 rounded-lg border border-blue-700 text-blue-800 text-sm disabled:opacity-50">
                  {visible ? "Show" : "Hide"} all achievements
                </button>
              ))}
              {saving && <span role="status" className="text-sm">Saving...</span>}
            </div>
          </div>
        )}
        {visibilityError && <p role="alert" className="text-center text-red-600 mb-4">{visibilityError}</p>}
        {visibilityMessage && <p role="status" className="text-center text-green-700 mb-4">{visibilityMessage}</p>}

        {/* Filters */}
        {(isAdmin || publicEnabled) && <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
          <div className="flex gap-3 flex-wrap justify-center md:justify-start">
            <button
              onClick={() => setFilterSport("All")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition ${filterSport === "All"
                  ? "bg-blue-700 text-white shadow"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
            >
              All Sports
            </button>

            {sports && sports.length > 0 ? (
              sports.map((s, idx) => {
                const name =
                  typeof s === "string" ? s : s.name || s.title || "";
                if (!name) return null;
                const active = filterSport === name;
                return (
                  <button
                    key={`${name}-${idx}`}
                    onClick={() => setFilterSport(name)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition ${active
                        ? "bg-blue-700 text-white shadow"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                  >
                    {name}
                  </button>
                );
              })
            ) : (
              <span className="text-sm text-gray-500 self-center">
                No sports filters
              </span>
            )}
          </div>
        </div>

        }
        {/* Content */}
        {loading ? (
          <div className="text-center text-gray-500 py-20">
            Loading players...
          </div>
        ) : error ? (
          <div className="text-center text-red-600 py-6">{error}</div>
        ) : filteredPlayers.length === 0 ? (
          <p className="text-center text-gray-600 py-10">
            {publicEnabled || isAdmin
              ? "No athlete highlights are available for this selection. Please check back for updates."
              : "Athlete highlights will be shared here. Please check back for updates."}
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {filteredPlayers.map((player) => (
              <article
                key={player.id || player.player_id}
                className="bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-2xl transition duration-300 overflow-hidden flex flex-col"
              >
                <div className="h-60 bg-gray-50 overflow-hidden relative flex items-center justify-center">
                  {player.photo ? (
                    <img
                      src={player.image_path}
                      alt={player.name || "Player"}
                      className="w-full h-full object-cover object-center transition-transform duration-300 hover:scale-105"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        if (e.currentTarget.nextSibling) {
                          e.currentTarget.nextSibling.style.display = "flex";
                        }
                      }}
                    />
                  ) : null}
                  <div
                    className={`w-full h-full bg-gradient-to-br ${getGradientBg(player.name)} flex flex-col items-center justify-center text-white relative`}
                    style={{ display: player.photo ? 'none' : 'flex' }}
                  >
                    <div className="absolute inset-0 opacity-10 flex items-center justify-center pointer-events-none select-none">
                      <svg className="w-32 h-32" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" />
                      </svg>
                    </div>
                    <div className="z-10 bg-white/15 backdrop-blur-md border border-white/20 rounded-full w-24 h-24 flex items-center justify-center shadow-lg transition-transform duration-300 hover:scale-105">
                      <span className="text-4xl font-black tracking-wider text-white drop-shadow-md">{getInitials(player.name)}</span>
                    </div>
                    <span className="text-[10px] uppercase font-bold tracking-widest mt-3 opacity-80 z-10">{player.district || "Parasports Athlete"}</span>
                  </div>
                </div>

                <div className="p-5 flex flex-col flex-1">
                  {isAdmin && (
                    <label className="flex items-center gap-3 mb-4 text-sm text-gray-600">
                      <input type="checkbox" disabled={saving}
                        aria-label={`Select ${player.name} for achievement visibility`}
                        checked={selectedIds.includes(player.id)}
                        onChange={(e) => setSelectedIds((current) => e.target.checked
                          ? [...current, player.id] : current.filter((id) => id !== player.id))} />
                      {!publicEnabled ? "Achievements hidden by master setting" : [false, 0, "0"].includes(player.achievements_public_visible)
                        ? "Achievements hidden from public" : "Achievements visible to public"}
                    </label>
                  )}
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">
                        {player.name}
                      </h2>
                      <p className="text-sm text-gray-600">
                        {player.district || "District N/A"}
                      </p>
                    </div>
                    <span className="px-3 py-1 rounded-full bg-yellow-100 text-yellow-800 text-xs font-semibold">
                      Medalist
                    </span>
                  </div>

                  <div className="flex gap-4 text-sm text-gray-700 mb-3">
                    <p>
                      <span className="font-medium">Gender:</span>{" "}
                      {player.gender || "N/A"}
                    </p>
                    <p>
                      <span className="font-medium">Age:</span>{" "}
                      {player.age}
                    </p>
                  </div>

                  {player.coach && (
                    <p className="text-sm text-gray-700 mb-3">
                      <span className="font-medium">Coach:</span>{" "}
                      {player.coach}
                    </p>
                  )}

                  {(isAdmin || (publicEnabled && ![false, 0, "0"].includes(player.achievements_public_visible))) && (
                    <div className="border-t pt-3 mt-auto">
                      <h3 className="font-semibold text-gray-800 mb-2 text-sm">
                        Medal Performances
                      </h3>
                      {!player.records || player.records.length === 0 ? (
                        <p className="text-gray-500 text-xs italic">
                          No records found.
                        </p>
                      ) : (
                        <div className="max-h-40 overflow-y-auto">
                          <table className="min-w-full text-xs">
                            <thead>
                              <tr className="bg-gray-100 text-gray-700">
                                <th className="p-2 border">#</th>
                                <th className="p-2 border">Sport</th>
                                <th className="p-2 border">Event</th>
                                <th className="p-2 border">Year</th>
                                <th className="p-2 border">Position</th>
                              </tr>
                            </thead>
                            <tbody>
                              {player.records
                                .filter(hasMedalInRecord)
                                .map((r, i) => {
                                  const sportName =
                                    r.sport ||
                                    r.sport_name ||
                                    r.sport_title ||
                                    "-";
                                  const eventName =
                                    r.event ||
                                    r.event_name ||
                                    r.title ||
                                    "-";
                                  const year =
                                    r.year ||
                                    r.event_year ||
                                    (r.date
                                      ? new Date(
                                        r.date
                                      ).getFullYear()
                                      : "-");
                                  let pos =
                                    r.position_secured ||
                                    r.position ||
                                    r.result_position ||
                                    r.rank ||
                                    "-";

                                  if (
                                    r.rank &&
                                    Number(r.rank) === 1 &&
                                    !String(pos)
                                      .toLowerCase()
                                      .includes("gold")
                                  ) {
                                    pos = "Gold";
                                  }

                                  return (
                                    <tr
                                      key={`${player.id || player.player_id}-${i
                                        }`}
                                      className="hover:bg-gray-50"
                                    >
                                      <td className="p-2 border text-center">
                                        {i + 1}
                                      </td>
                                      <td className="p-2 border">
                                        {sportName}
                                      </td>
                                      <td className="p-2 border">
                                        {eventName}
                                      </td>
                                      <td className="p-2 border text-center">
                                        {year}
                                      </td>
                                      <td className="p-2 border text-center">
                                        {pos || "-"}
                                      </td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
      {!adminView && <PageSectionsRenderer pageKey="players" className="pt-0" excludeKeys={["players_intro"]} />}
      {!adminView && <Footer />}
    </>
  );
};

export default Players;
